import { useState, useMemo } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { brandColor, roleLabel } from "@/lib/utils-msip";
import { ChangePasswordButton } from "@/components/ChangePasswordDialog";
import { FreshnessFooter } from "@/components/FreshnessFooter";
import { NotificationBell } from "@/components/NotificationBell";
import { navItemsFor, SECTION_LABELS } from "@/lib/nav";
import { LogOut, Menu, X } from "lucide-react";

export default function Layout() {
  const { user, logout, meta, filters, setFilters } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isDpc = pathname.startsWith("/dpc");
  const isService = pathname.startsWith("/service");
  const isStock = pathname.startsWith("/stock");
  const plainHeader = isDpc || isService || isStock;
  const showBell = user?.role === "admin" || user?.role === "md";

  const brands = useMemo(() => (meta?.brands || []).map((b) => b.name), [meta]);

  const districts = useMemo(() => {
    const geos = meta?.geographies || [];
    const set = new Set(
      geos
        .filter((g) => !g.is_state_row)
        .filter((g) => g.district && g.district !== "UNMAPPED")
        .map((g) => g.district)
    );
    return [...set].sort();
  }, [meta]);

  const navItems = useMemo(() => navItemsFor(user), [user]);

  const navBody = (
    <nav className="flex flex-col gap-1 px-3">
      {navItems.map(({ to, label, icon: Icon, end, mod }, i) => (
        <div key={to}>
          {(i === 0 || navItems[i - 1].mod !== mod) && (
            <div className={`px-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70 ${i === 0 ? "" : "pt-3"}`} data-testid={`nav-section-${mod}`}>{SECTION_LABELS[mod]}</div>
          )}
          <NavLink
            to={to}
            end={end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                isActive ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <Icon size={16} /> {label}
          </NavLink>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="hidden lg:flex flex-col w-56 shrink-0 border-r bg-card print-hidden sticky top-0 h-screen">
        <div className="px-6 py-5 border-b">
          <div className="font-heading text-lg font-extrabold tracking-tight leading-tight">Incheon Kia <span style={{ color: "#BB162B" }}>MIS</span></div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Market Intelligence System</div>
        </div>
        <div className="py-4 flex-1 overflow-y-auto">{navBody}</div>
        <FreshnessFooter />
        <div className="border-t px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70" data-testid="user-role-label">{roleLabel(user)}</div>
            {user?.password_is_default && <div className="text-[10px] text-amber-600 font-medium" data-testid="default-password-hint">Default password — change it</div>}
          </div>
          <div className="flex items-center shrink-0">
            <ChangePasswordButton />
            <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut size={15} />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 border-b bg-card print-hidden">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
            <button className="lg:hidden p-2 -ml-1" data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="lg:hidden font-heading font-extrabold text-sm mr-1 whitespace-nowrap">Incheon Kia <span style={{ color: "#BB162B" }}>MIS</span></div>

            {isDpc ? (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" data-testid="dpc-header-label">Dealer Performance Cockpit</div>
            ) : isService ? (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" data-testid="service-header-label">Service Cockpit · After-Sales</div>
            ) : isStock ? (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" data-testid="stock-header-label">Stock & Ageing · Sales</div>
            ) : (
            <>
            <div className="flex items-center rounded-md border overflow-hidden">
              {["KIA"].map((b) => (
                <button
                  key={b}
                  data-testid={`brand-quick-${b}`}
                  onClick={() => setFilters({ brand: b })}
                  className="px-3 py-1.5 text-xs font-semibold transition-colors duration-200"
                  style={filters.brand === b ? { background: brandColor(b), color: "#fff" } : {}}
                >
                  {b}
                </button>
              ))}
              <Select value={brands.includes(filters.brand) ? filters.brand : "KIA"} onValueChange={(v) => setFilters({ brand: v })}>
                <SelectTrigger className="h-8 w-[110px] border-0 border-l rounded-none text-xs" data-testid="brand-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Select value={filters.period} onValueChange={(v) => setFilters({ period: v })}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily change</SelectItem>
                <SelectItem value="mtd">MTD</SelectItem>
                <SelectItem value="last_month">Last full month</SelectItem>
                <SelectItem value="ytd">YTD</SelectItem>
              </SelectContent>
            </Select>

            {user?.role !== "manager" && (
              <>
                <Select value={filters.district || "ALL"} onValueChange={(v) => setFilters({ district: v === "ALL" ? "" : v })}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="district-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All districts</SelectItem>
                    {districts.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                  <Switch checked={filters.territoryOnly} onCheckedChange={(v) => setFilters({ territoryOnly: v })} data-testid="territory-toggle" />
                  Incheon only
                </label>
              </>
            )}
            {user?.role === "manager" && (
              <span className="rounded bg-secondary px-2 py-1 text-[11px] font-medium text-muted-foreground" data-testid="dealer-scope-chip">
                Scope: {(user.dealer_codes || []).join(", ") || "—"}
              </span>
            )}

            </>
            )}
            <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="header-actions">
              {!plainHeader && meta?.latest_mtd_date && (
                <span className="rounded bg-emerald-50 text-emerald-800 px-2 py-1 font-medium" data-testid="freshness-banner">
                  Data as of {meta.latest_mtd_date}
                </span>
              )}
              {showBell && <NotificationBell />}
            </div>
          </div>
          {mobileOpen && (
            <div className="lg:hidden border-t py-3 bg-card">
              {navBody}
              <FreshnessFooter testId="freshness-footer-mobile" />
              <div className="px-4 pt-3 mt-2 border-t flex items-center justify-between gap-2">
                <div className="min-w-0 text-xs text-muted-foreground truncate">{user?.email}</div>
                <div className="flex items-center"><ChangePasswordButton /><Button variant="ghost" size="icon" data-testid="mobile-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}><LogOut size={15} /></Button></div>
              </div>
            </div>
          )}
        </header>

        <main className="p-4 lg:p-6 max-w-[1600px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
